<template>
  <FormularioCadastro
    module-name="Planos"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="submitComDiagnostico"
    @cancelar="handleCancel"
  >
    <!-- GUARDIÃO: Botão de Auditoria injetado no cabeçalho -->
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="abrirAuditoria"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <!-- Frame 1: Dados Básicos -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">Dados Básicos</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-8">
            <CampoInput
              id="nomePlano"
              label="Nome do Plano"
              v-model="item.nome"
              @keydown.enter.prevent
              required
            />
          </div>

          <!-- GUARDIÃO: Consumindo a Lista do Sistema com o prefixo Lst -->
          <div class="col-md-4">
            <CampoSelect
              id="lstNivelModuloFiscal"
              label="Nível do Módulo Fiscal"
              v-model="item.lstNivelModuloFiscal"
              :options="niveisFiscais"
              option-value="idListaSistemaItem"
              option-label="textoLista"
              required
            />
          </div>

          <div class="col-md-12">
            <CampoInput
              id="descricaoPlano"
              label="Descrição"
              type="textarea"
              v-model="item.descricao"
              required
            />
          </div>

          <div class="col-md-4">
            <CampoInput
              id="valorPlano"
              label="Valor Mensal (R$)"
              type="number"
              min="0"
              step="0.01"
              v-model.number="item.valor"
            />
          </div>

          <div class="col-md-4">
            <CampoInput
              id="validadeInicio"
              label="Validade Início"
              type="date"
              v-model="item.validadeInicio"
            />
          </div>

          <div class="col-md-4">
            <CampoInput
              id="validadeFim"
              label="Validade Fim"
              type="date"
              v-model="item.validadeFim"
            />
          </div>

          <div class="col-md-4">
            <CampoSelect
              id="lstStatus"
              label="Status"
              v-model="item.lstStatus"
              :options="statusOptions"
              option-value="idListaSistemaItem"
              option-label="textoLista"
            />
          </div>
        </div>
      </div>
    </div> <!-- Fim Frame 1 -->
  </FormularioCadastro>

  <!-- GUARDIÃO: Componente de Auditoria -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Plano"
    contexto="master"
    :chave-primaria="item.idPlano"
    :titulo-registro="item.nome"
    @fechar="mostrarAuditoria = false"
  />

  <!-- Modal de Diagnóstico -->
  <ModalFormulario
    v-model="mostrarRelatorio"
    titulo="Diagnóstico de Conformidade Fiscal"
    :ocultar-confirmar="true"
    tamanho="xl"
    @update:model-value="aoFecharModal"
  >
    <DiagnosticoFiscalRelatorio
      :modo-multi-empresa="dadosRelatorio.modoMultiEmpresa"
      :nivel-antigo="dadosRelatorio.nivelAntigo"
      :nivel-novo="dadosRelatorio.nivelNovo"
      :empresas-com-pendencias="dadosRelatorio.empresasComPendencias"
      :pendencias="dadosRelatorio.pendencias"
      :pode-fazer-upgrade="dadosRelatorio.podeFazerUpgrade"
      :total-pendencias="dadosRelatorio.totalPendencias"
      :is-loading="dadosRelatorio.isLoading"
      @fechar="mostrarRelatorio = false"
    />
  </ModalFormulario>
</template>

<script setup>
// --- IMPORTAÇÕES ---
import { ref, reactive, onMounted } from 'vue'
import api from '@/services/api'
import { useFormulario } from '@/composables/useFormulario.js'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import DiagnosticoFiscalRelatorio from '@/components/fiscal/DiagnosticoFiscalRelatorio.vue'

// GUARDIÃO: Listas reativas que serão preenchidas pela API
const niveisFiscais = ref([])
const statusOptions = ref([])

// --- DEFINIÇÕES DO COMPONENTE ---
const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idPlano: 0,
      nome: '',
      descricao: '',
      valor: 0.00,
      lstNivelModuloFiscal: '', // GUARDIÃO: Propriedade corrigida para Lst
      lstStatus: '',
      validadeInicio: new Date().toISOString().split('T')[0],
      validadeFim: new Date().toISOString().split('T')[0]
    })
  }
})
const emit = defineEmits(['itemAtualizado', 'cancelar', 'changesDetected', 'show-notification'])

// --- LÓGICA DO FORMULÁRIO ---
const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

// --- Estado da Auditoria ---
const mostrarAuditoria = ref(false)

const abrirAuditoria = () => {
  mostrarAuditoria.value = true
}

// --- ESTADO DO RELATÓRIO DE DIAGNÓSTICO ---
const mostrarRelatorio = ref(false)
const dadosRelatorio = reactive({
  modoMultiEmpresa: false,
  nivelAntigo: '',
  nivelNovo: '',
  empresasComPendencias: [],
  pendencias: [],
  podeFazerUpgrade: false,
  totalPendencias: 0,
  isLoading: false
})

const aoFecharModal = (valor) => {
  mostrarRelatorio.value = valor
}

const submitComDiagnostico = async () => {
  // Se é edição, detectar se o nível fiscal subiu
  if (isEditing.value && item.value.lstNivelModuloFiscal && props.itemParaEditar?.lstNivelModuloFiscal) {
    const nivelAntigo = props.itemParaEditar.lstNivelModuloFiscal
    const nivelNovo = item.value.lstNivelModuloFiscal

    // Só verifica se o nível mudou (subiu)
    const idPlano = item.value.idPlano
    if (nivelAntigo !== nivelNovo && idPlano != null && idPlano !== 0) {
      dadosRelatorio.isLoading = true
      dadosRelatorio.nivelAntigo = nivelAntigo
      dadosRelatorio.nivelNovo = nivelNovo

      try {
        // Buscar empresas vinculadas a este plano
        const empresasResp = await api.get(`/Planos/${idPlano}/empresas`)
        const empresas = empresasResp.data

        if (empresas.length > 0) {
          let totalPendencias = 0
          const empresasComPendencias = []

          for (const emp of empresas) {
            try {
              const diagResp = await api.get(`/Fiscal/diagnostico/${emp.idEmpresa}`)
              const diag = diagResp.data
              totalPendencias += diag.totalPendencias || 0
              if (diag.totalPendencias > 0) {
                empresasComPendencias.push({
                  idEmpresa: emp.idEmpresa,
                  nomeEmpresa: emp.nomeEmpresa,
                  totalPendencias: diag.totalPendencias,
                  pendencias: diag.pendencias || []
                })
              }
            } catch (e) {
              console.error(`Erro no diagnóstico da empresa ${emp.idEmpresa}:`, e)
            }
          }

          dadosRelatorio.empresasComPendencias = empresasComPendencias
          dadosRelatorio.totalPendencias = totalPendencias
          dadosRelatorio.podeFazerUpgrade = totalPendencias === 0
          dadosRelatorio.modoMultiEmpresa = true

          if (totalPendencias > 0) {
            dadosRelatorio.isLoading = false
            emit('show-notification', {
              message: `Upgrade bloqueado. ${totalPendencias} pendência(s) encontrada(s) em ${empresasComPendencias.length} empresa(s).`,
              type: 'warning'
            })
            mostrarRelatorio.value = true
            return // BLOQUEIA o salvamento
          }
        }
      } catch (e) {
        console.error('Erro ao verificar diagnóstico:', e)
      } finally {
        dadosRelatorio.isLoading = false
      }
    }
  }

  // Se chegou aqui, pode salvar
  try {
    await submitForm('Planos', 'idPlano')
  } catch (error) {
    // Se o backend retornou 400 com dados de diagnóstico, mostrar relatório
    if (error?.response?.status === 400 && error?.response?.data?.bloqueado) {
      const data = error.response.data
      dadosRelatorio.empresasComPendencias = data.pendenciasPorEmpresa || []
      dadosRelatorio.totalPendencias = data.pendenciasPorEmpresa
        ? data.pendenciasPorEmpresa.reduce((acc, e) => acc + (e.totalPendencias || 0), 0)
        : 0
      dadosRelatorio.podeFazerUpgrade = false
      dadosRelatorio.modoMultiEmpresa = true
      dadosRelatorio.isLoading = false
      mostrarRelatorio.value = true
      return // BLOQUEIA, não propaga o erro
    }
    // Para outros erros, propagar para o FormularioCadastro tratar
    throw error
  }
}

const carregarListas = async () => {
  try {
    // Chamada ao contexto Master para buscar as Listas de Sistema
    const [resNiveis, resStatus] = await Promise.all([
      api.get('/ListasSistema/lookup/NV_MODULO_FISCAL'),
      api.get('/ListasSistema/lookup/ST')
    ])
    niveisFiscais.value = resNiveis.data
    statusOptions.value = resStatus.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar listas de sistema.', type: 'error' })
  }
}

onMounted(() => {
  carregarListas()
})
</script>

<style scoped>
/* GUARDIÃO: CSS limpo! A classe global FormularioCadastro.vue trata de tudo. */
</style>
